import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';

export const checkboxVariants = cva(
  [
    'form-checkbox rounded-[4px] border border-gray-300 bg-white text-brand',
    'transition-all duration-150 cursor-pointer',
    'hover:border-brand/60',
    'checked:border-transparent indeterminate:border-transparent',
    'focus:ring-0 focus:ring-offset-0',
    'focus-visible:ring-2 focus-visible:ring-brand/30 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300',
  ],
  {
    variants: {
      size: {
        sm: 'h-3.5 w-3.5',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
      error: {
        true: 'border-red-500 hover:border-red-500 focus-visible:ring-red-200',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

type KoobInputCheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'size' | 'type' | 'onChange'
> &
  VariantProps<typeof checkboxVariants> & {
    onCheckedChange?: (checked: boolean) => void;
    indeterminate?: boolean;
    /** Styled text label; pass children instead for custom label markup */
    label?: React.ReactNode;
    /** @deprecated use data-testid */
    testId?: string;
    /** Not part of the API (see packages/ui/README.md) — stripped at runtime */
    className?: never;
  };

const KoobInputCheckbox = React.forwardRef<
  HTMLInputElement,
  KoobInputCheckboxProps
>(
  (
    {
      checked,
      onCheckedChange,
      indeterminate = false,
      label,
      children,
      size,
      error,
      disabled,
      testId,
      // className is not part of the API (see packages/ui/README.md) but is
      // still stripped here so a stray one can't clobber the computed classes
      className,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    return (
      <label
        className={cn(
          'inline-flex items-center gap-3',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          ref={setRefs}
          type="checkbox"
          className={checkboxVariants({ size, error })}
          checked={checked}
          onChange={(event) => onCheckedChange?.(event.target.checked)}
          disabled={disabled}
          aria-invalid={error || undefined}
          data-testid={testId}
          {...props}
        />
        {children ??
          (label != null && (
            <span className="text-sm font-semibold text-gray-600 select-none">
              {label}
            </span>
          ))}
      </label>
    );
  },
);

KoobInputCheckbox.displayName = 'KoobInputCheckbox';

export default KoobInputCheckbox;
